import type { ComparisonTypes } from "../types/ComparisonTypes";

// Type for insurance section keys
type SectionKey = keyof ComparisonTypes | string;

// General value type for properties
type PropertyValue =
	| string
	| number
	| boolean
	| null
	| undefined
	| Record<string, unknown>
	| Array<unknown>;

/**
 * Compare insurance policies and identify differences for a specific section and property
 * @param companies Array of company data to compare
 * @param sectionKey Key of the section to compare (e.g., "maskinskade", "glasskade")
 * @param propertyPath Path to the property within the section (e.g., "dekkes", "batteri")
 * @returns Boolean indicating whether differences exist between companies for this property
 */
export function hasDifferenceInProperty(
	companies: ComparisonTypes[],
	sectionKey: SectionKey,
	propertyPath: string,
): boolean {
	if (!companies || companies.length <= 1) return false;

	// Use the first company data as a reference
	const referenceValue = getPropertyValueByPath(
		companies[0],
		sectionKey,
		propertyPath,
	);

	// Check if any other company has a different value
	return companies.some((company, index) => {
		if (index === 0) return false; // Skip reference company

		const companyValue = getPropertyValueByPath(
			company,
			sectionKey,
			propertyPath,
		);
		return !isEqual(referenceValue, companyValue);
	});
}

/**
 * Get a property value from an object using a dot-notation path
 * @param obj Object to get property from
 * @param sectionKey Root section key
 * @param path Path to property using dot notation (e.g., "bonus.bonustap")
 * @returns Property value or undefined if path doesn't exist
 */
function getPropertyValueByPath(
	obj: ComparisonTypes,
	sectionKey: SectionKey,
	path: string,
): PropertyValue {
	// Type safety check
	if (!obj || !(sectionKey in obj)) return undefined;

	// Start from the section (with safe type assertion)
	let current: unknown = obj[sectionKey as keyof ComparisonTypes];

	// If there's no further path, return the section
	if (!path) return current as PropertyValue;

	// Split the path and navigate through the object
	const parts = path.split(".");
	for (const part of parts) {
		if (current === null || current === undefined) return undefined;

		// Type safety check - make sure we can index into current
		if (typeof current === "object" && current !== null) {
			current = (current as Record<string, unknown>)[part];
		} else {
			return undefined;
		}
	}

	return current as PropertyValue;
}

/**
 * Check if two values are equal (handles primitive types, objects, arrays)
 * @param a First value
 * @param b Second value
 * @returns Boolean indicating equality
 */
function isEqual(a: PropertyValue, b: PropertyValue): boolean {
	// Handle null/undefined
	if (a === b) return true;
	if (a === null || b === null || a === undefined || b === undefined)
		return false;

	// Handle different types
	const typeA = typeof a;
	const typeB = typeof b;
	if (typeA !== typeB) return false;

	// Handle arrays
	if (Array.isArray(a) && Array.isArray(b)) {
		if (a.length !== b.length) return false;
		for (let i = 0; i < a.length; i++) {
			// Safe type casting for recursion
			const valA = a[i] as PropertyValue;
			const valB = b[i] as PropertyValue;
			if (!isEqual(valA, valB)) return false;
		}
		return true;
	}

	// Handle objects
	if (typeA === "object") {
		const keysA = Object.keys(a as Record<string, unknown>);
		const keysB = Object.keys(b as Record<string, unknown>);
		if (keysA.length !== keysB.length) return false;
		return keysA.every((key) =>
			isEqual(
				(a as Record<string, unknown>)[key] as PropertyValue,
				(b as Record<string, unknown>)[key] as PropertyValue,
			),
		);
	}

	// For primitive types
	return a === b;
}

/**
 * Get all differences between companies for a specific section
 * @param companies Array of company data to compare
 * @param sectionKey Key of the section to compare
 * @returns Object mapping property paths to booleans indicating differences
 */
export function getDifferencesBySection(
	companies: ComparisonTypes[],
	sectionKey: keyof ComparisonTypes,
): Record<string, boolean> {
	if (!companies || companies.length <= 1) {
		return {};
	}

	// Safe check if section exists
	const firstCompany = companies[0];
	if (!(sectionKey in firstCompany)) {
		return {};
	}

	const differences: Record<string, boolean> = {};
	const referenceSection = firstCompany[sectionKey];

	// Function to recursively check differences in an object
	function checkDifferences(
		obj: Record<string, unknown>,
		currentPath = "",
	): void {
		if (!obj || typeof obj !== "object") return;

		for (const key of Object.keys(obj)) {
			const propPath = currentPath ? `${currentPath}.${key}` : key;
			const value = obj[key];

			if (
				typeof value === "object" &&
				value !== null &&
				!Array.isArray(value)
			) {
				// Recursively check nested objects
				checkDifferences(value as Record<string, unknown>, propPath);
			} else {
				// Check for differences in leaf properties
				differences[propPath] = hasDifferenceInProperty(
					companies,
					sectionKey,
					propPath,
				);
			}
		}
	}

	checkDifferences(referenceSection as unknown as Record<string, unknown>);
	return differences;
}

/**
 * Get the most important differences between companies across all sections
 * @param companies Array of company data to compare
 * @returns Array of objects containing section, property and difference information
 */
interface DifferenceItem {
	section: string;
	property: string;
	label: string;
	values: Array<{ company: string; value: PropertyValue }>;
}

export function getKeyDifferences(
	companies: ComparisonTypes[],
): DifferenceItem[] {
	if (!companies || companies.length <= 1) {
		return [];
	}

	const keyDifferences: DifferenceItem[] = [];

	// Important sections and their key properties to check for differences
	const sectionsToCheck = [
		{
			key: "maskinskade",
			properties: ["dekkes", "batteri", "maks_alder_år", "maks_kjorelengde_km"],
		},
		{
			key: "glasskade",
			properties: [
				"dekkes",
				"glasstak",
				"reparasjon_av_glass.egenandel",
				"bytte_av_glass.egenandel",
			],
		},
		{
			key: "parkeringsskade",
			properties: ["dekkes", "maks_beløp", "egenandel"],
		},
		{
			key: "kollisjon",
			properties: ["dekkes", "egenandel", "nybilgaranti.dekket"],
		},
		{
			key: "leiebil",
			properties: ["dekkes", "geografisk_område", "benyttet.maks_dager"],
		},
		{ key: "tyveri", properties: ["dekkes", "egenandel"] },
	];

	// Human-readable labels for properties
	const propertyLabels: Record<string, string> = {
		dekkes: "Dekkes",
		batteri: "Batteri dekket",
		maks_alder_år: "Maks alder (år)",
		maks_kjorelengde_km: "Maks kjørelengde (km)",
		glasstak: "Glasstak dekket",
		"reparasjon_av_glass.egenandel": "Egenandel ved reparasjon",
		"bytte_av_glass.egenandel": "Egenandel ved bytte",
		maks_beløp: "Maksimalt beløp",
		egenandel: "Egenandel",
		"nybilgaranti.dekket": "Nybilgaranti",
		geografisk_område: "Geografisk område",
		"benyttet.maks_dager": "Maks dager leiebil",
	};

	// Section labels
	const sectionLabels: Record<string, string> = {
		maskinskade: "Maskinskade",
		glasskade: "Glasskade",
		parkeringsskade: "Parkeringsskade",
		kollisjon: "Kollisjon",
		leiebil: "Leiebil",
		tyveri: "Tyveri",
	};

	// Check each section and property for differences
	for (const section of sectionsToCheck) {
		for (const property of section.properties) {
			if (hasDifferenceInProperty(companies, section.key, property)) {
				const values = companies.map((company) => ({
					company: company.selskap,
					value: getPropertyValueByPath(company, section.key, property),
				}));

				keyDifferences.push({
					section: section.key,
					property,
					label: propertyLabels[property] || property,
					values,
				});
			}
		}
	}

	return keyDifferences;
}

/**
 * Create a summary of the differences between companies
 * @param companies Array of company data to compare
 * @returns String summary of key differences
 */
export function createDifferenceSummary(companies: ComparisonTypes[]): string {
	const differences = getKeyDifferences(companies);

	if (differences.length === 0) {
		return "Ingen vesentlige forskjeller funnet mellom selskapene.";
	}

	return `Fant ${differences.length} viktige forskjeller mellom forsikringene.`;
}
